"use strict";
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
var Logger_1 = require("../utils/Logger");
var SparseList_1 = require("./SparseList");
exports.DEFAULT_PAGE_SIZE = 250;
/**
 * The number of consecutive calls to getItems whose requested range and returned rowResult match each other's
 * before subsequent calls requesting the same range will stop issuing queries.
 *
 * Note: This will only actually take effect if the backend consistently returns fewer rows than expected.
 */
exports.CONSECUTIVE_MATCHING_REQUEST_CUTOFF = 5;
/**
 * This class implements pagination/caching logic that can be used with any data grid or other list
 * view component. It is particularly useful for situations where we are querying large sets of
 * user-provided data that is not all available at once (in those cases, the basic ArrayRowStore
 * would be sufficient).
 *
 * Using the client-provided `requestRows` function, the row store queries the backend for larger
 * chunks than the component asks for, then caches these fetched items so it can instantly return them
 * back to the component as the user scrolls through the list.
 *
 * Per the RowStore API, the array returned by the row handler is sparse, with the length being equal
 * to the total count of all the items, including those that have not yet been fetched.
 */
var PaginatedRowStore = /** @class */ (function () {
    function PaginatedRowStore(queryRows, pageSize) {
        if (pageSize === void 0) { pageSize = exports.DEFAULT_PAGE_SIZE; }
        this.queryRows = queryRows;
        this.pageSize = pageSize;
        this.rows = new SparseList_1.SparseList(0);
        this.errors = [];
        this.previousRequestedRange = { start: -1, end: -1 };
        this.previousRowResult = { rows: [], totalCount: 0 };
        this.consecutiveMatchingRequests = 1;
    }
    PaginatedRowStore.prototype.getItems = function (start, end, rowHandler) {
        var _this = this;
        var requestedRange = { start: start, end: end };
        // Don't repeat requests indefinitely when the results aren't changing
        if (rangesEqual(requestedRange, this.previousRequestedRange)
            && this.consecutiveMatchingRequests >= exports.CONSECUTIVE_MATCHING_REQUEST_CUTOFF) {
            rowHandler(__spreadArrays(this.rows.data), this.errors);
            return;
        }
        var normalQueryRange = this.getPossibleQueryRangeForNormalQuery(requestedRange);
        if (normalQueryRange) {
            this.queryRows(normalQueryRange)
                .then(function (rowResult) {
                _this.maybeIncrementDuplicateRequestCounterAndCacheResult(requestedRange, rowResult);
                _this.updateRows(rowResult, normalQueryRange);
                // If we don't copy the data to a new array, the rendered grid sections sometimes have holes
                rowHandler(__spreadArrays(_this.rows.data), _this.errors);
            })
                .catch(function (error) {
                Logger_1.Logger.error(error);
                throw error;
            });
        }
        else {
            // If we already had the requested group of items, maybe pre-fetch the next page of items
            // so that items will continue to render instantly as the user scrolls down.
            // Don't invoke the rowHandler after prefetching the rows because we don't need to render them immediately.
            var prefetchQueryRange_1 = this.getPossibleQueryRangeForPrefetch(requestedRange);
            if (prefetchQueryRange_1) {
                this.queryRows(prefetchQueryRange_1)
                    .then(function (rowResult) {
                    _this.maybeIncrementDuplicateRequestCounterAndCacheResult(requestedRange, rowResult);
                    _this.updateRows(rowResult, prefetchQueryRange_1);
                })
                    .catch(function (error) {
                    Logger_1.Logger.error(error);
                    throw error;
                });
            }
            // Invoke the rowHandler AFTER executing the query so that we can verify in the tests that the query happened.
            // The query result will still be handled asynchronously, so as not to block the data grid from rendering.
            rowHandler(__spreadArrays(this.rows.data), this.errors);
        }
    };
    /**
     * Looks at the whole "page" of items containing the requested range, and returns the boundaries
     * for that page if the page is not completely fetched. If that page is already fetched, returns null.
     */
    PaginatedRowStore.prototype.getPossibleQueryRangeForNormalQuery = function (requestedRange) {
        // The DataGrid requests small chunks of items (on the order of 20 at a time), so we will usually
        // "extend" the query range to request an entire page of items from the backend, to reduce the
        // number of times we have to call the backend as the user scrolls down.
        //
        // However, if this is the very first request to the row store (i.e. if we don't yet know the total item count),
        // we query for just the bare minimum amount of rows, so that we get a fast initial load time.
        if (!this.rows.length) {
            return requestedRange;
        }
        var queryRange = this.getPageBoundariesForRange(requestedRange);
        return this.itemsMissingInRange(queryRange) ? queryRange : null;
    };
    /**
     * The "prefetch" looks one page ahead from the page on which the currently requested rows reside.
     * If some items are missing on that page, its boundaries are returned; otherwise, returns null.
     */
    PaginatedRowStore.prototype.getPossibleQueryRangeForPrefetch = function (requestedRange) {
        // Manufacture a one-item range that sits on the next page so that we can calculate
        // that page's full boundaries
        var itemOnNextPage = requestedRange.end + this.pageSize;
        var rangeWithOneItemOnNextPage = { start: itemOnNextPage, end: itemOnNextPage };
        var queryRange = this.getPageBoundariesForRange(rangeWithOneItemOnNextPage);
        return this.itemsMissingInRange(queryRange) ? queryRange : null;
    };
    PaginatedRowStore.prototype.getPageBoundariesForRange = function (range) {
        var pageIndexOfFirstItem = Math.floor(range.start / this.pageSize);
        var pageIndexOfLastItem = Math.floor(range.end / this.pageSize);
        if (pageIndexOfFirstItem === pageIndexOfLastItem) {
            // Conventional case: the range lies within a single page; return that full page
            var start = this.pageSize * pageIndexOfFirstItem;
            var end = Math.min(start + this.pageSize - 1, this.rows.length - 1);
            return { start: start, end: end };
        }
        else {
            // If the request straddles a page boundary, we return a range that isn't snapped to page boundaries
            // so we can still return all the requested rows without wasting bandwidth
            return {
                start: range.start,
                end: Math.min(range.start + this.pageSize - 1, this.rows.length - 1),
            };
        }
    };
    PaginatedRowStore.prototype.itemsMissingInRange = function (range) {
        return !this.rows.rangeIsDefined(range.start, range.end);
    };
    PaginatedRowStore.prototype.updateRows = function (rowResult, queryRange) {
        // Invalidate our previous rows if the new query says the row count is different
        // than what we had before. This is the easiest scenario where we know the data has changed.
        // TODO (TFS 873789): Do we need to invalidate Apollo's cache too?
        if (rowResult.totalCount !== this.rows.length) {
            this.rows = new SparseList_1.SparseList(rowResult.totalCount);
            this.previousRequestedRange = { start: -1, end: -1 };
            this.previousRowResult = { rows: [], totalCount: 0 };
            this.consecutiveMatchingRequests = 1;
        }
        this.rows.setRange(queryRange.start, rowResult.rows);
        if (rowResult.errors) {
            this.errors = this.errors.concat(rowResult.errors);
        }
    };
    PaginatedRowStore.prototype.maybeIncrementDuplicateRequestCounterAndCacheResult = function (requestedRange, rowResult) {
        var requestedRangeWasTheSameAsLastTime = rangesEqual(requestedRange, this.previousRequestedRange);
        if (requestedRangeWasTheSameAsLastTime && resultsEqual(rowResult, this.previousRowResult)) {
            this.consecutiveMatchingRequests++;
        }
        else {
            this.consecutiveMatchingRequests = 1;
        }
        this.previousRequestedRange = requestedRange;
        this.previousRowResult = rowResult;
    };
    // This method returns a readonly view of the rows currently stored by the row store, for unit tests.
    PaginatedRowStore.prototype.getStoredRows = function () {
        return this.rows.data;
    };
    return PaginatedRowStore;
}());
exports.PaginatedRowStore = PaginatedRowStore;
function rangesEqual(a, b) {
    return a.start === b.start && a.end === b.end;
}
function resultsEqual(a, b) {
    if (a.totalCount !== b.totalCount || a.rows.length !== b.rows.length) {
        return false;
    }
    for (var i = 0; i < a.rows.length; i++) {
        if (a.rows[i].key !== b.rows[i].key) {
            return false;
        }
    }
    return true;
}
//# sourceMappingURL=PaginatedRowStore.js.map